import { useState, useEffect } from 'react'
import { Menu, ChevronDown } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { OrganizationSidebarContent } from '@/components/OrganizationSidebar'
import { StatCard } from './components/StatCard'
import { DonutChart } from './components/DonutChart'
import { BarChart } from './components/BarChart'
import { GaugeChart } from './components/GaugeChart'
import { organizationApi, analyticsApi } from '@/utils/api'
import type { OrganizationStatistics, DashboardStats, AgeDistribution } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'

// Icons for stat cards
const CoupleIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
    <circle cx='9' cy='7' r='4' />
    <path d='M23 21v-2a4 4 0 0 0-3-3.87' />
    <path d='M16 3.13a4 4 0 0 1 0 7.75' />
  </svg>
)

const SendIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='22' y1='2' x2='11' y2='13' />
    <polygon points='22 2 15 22 11 13 2 9 22 2' />
  </svg>
)

const CertificateIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='12' cy='8' r='6' />
    <path d='M15.477 12.89 17 22l-5-3-5 3 1.523-9.11' />
  </svg>
)

const ProgressIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='12' cy='12' r='10' />
    <path d='M12 6v6l4 2' />
  </svg>
)

const CheckIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
    <polyline points='22 4 12 14.01 9 11.01' />
  </svg>
)

export default function OrganizationAnalytics() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female'>('all')
  const [performanceFilter, setPerformanceFilter] = useState<'all' | 'male' | 'female'>('all')

  // API data states
  const [statistics, setStatistics] = useState<OrganizationStatistics | null>(null)
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null)
  const [ageDistribution, setAgeDistribution] = useState<AgeDistribution | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch data from APIs
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.organizationId) {
        setError('No organization found')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const [statsRes, dashboardRes, ageRes] = await Promise.all([
          organizationApi.getStatistics(user.organizationId),
          analyticsApi.getDashboard(),
          analyticsApi.getAgeDistribution(),
        ])

        setStatistics(statsRes.data)
        setDashboardStats(dashboardRes.data)
        setAgeDistribution(ageRes.data)
      } catch (err) {
        console.error('Failed to fetch analytics data:', err)
        setError('Failed to load analytics data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user?.organizationId])

  // Stat cards data from API
  const stats = statistics
    ? [
        {
          title: 'Total Invited Couples',
          value: String(statistics.totalInvitedCouples),
          icon: <CoupleIcon />,
        },
        {
          title: 'Pending Invites',
          value: String(statistics.pendingInvites),
          icon: <SendIcon />,
        },
        {
          title: 'Certified Couples',
          value: String(statistics.certifiedCouples),
          icon: <CertificateIcon />,
        },
        {
          title: 'Couples In-Progress',
          value: String(statistics.couplesInProgress),
          icon: <ProgressIcon />,
        },
        {
          title: 'Certification Approval',
          value: String(statistics.certificationApproval),
          icon: <CheckIcon />,
        },
      ]
    : []

  // Low rating distribution colors matching Figma
  const lowRatingColors = ['#1E3A8A', '#F97316', '#8B5CF6', '#10B981']

  // Convert lowRatingDistribution from API to chart format
  const lowRatingData = dashboardStats?.lowRatingDistribution
    ? Object.entries(dashboardStats.lowRatingDistribution)
        .map(([label, value], index) => {
          const total = Object.values(dashboardStats.lowRatingDistribution).reduce((sum, v) => sum + v, 0)
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0
          return {
            label,
            value: percentage,
            color: lowRatingColors[index % lowRatingColors.length] || '#1E3A8A',
            percentage: `${percentage}%`,
          }
        })
        .sort((a, b) => b.value - a.value)
    : []

  // Performance data from API (convert lowRatingDistribution to bar chart format)
  const performanceData = dashboardStats?.lowRatingDistribution
    ? Object.entries(dashboardStats.lowRatingDistribution).map(([label, value]) => ({
        label: label.length > 8 ? label.substring(0, 8) : label,
        value,
      }))
    : []

  const maxPerformanceValue = performanceData.length > 0 ? Math.max(...performanceData.map((d) => d.value)) : 50000

  // Age distribution colors matching Figma
  const ageColors = ['#1E3A8A', '#F97316', '#8B5CF6', '#10B981']

  // Age distribution data from API
  const ageDistributionData = ageDistribution
    ? Object.entries(ageDistribution).map(([label, value], index) => {
        const total = Object.values(ageDistribution).reduce((sum, v) => sum + v, 0)
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0
        return {
          label,
          value: percentage,
          color: ageColors[index % ageColors.length] || '#7C3AED',
          percentage: `${percentage}%`,
        }
      })
    : []

  // Calculate impact rating from performance overview
  const impactRating = dashboardStats?.performanceOverview
    ? Math.round(
        (dashboardStats.performanceOverview.completedAssessments /
          dashboardStats.performanceOverview.totalAssessments) *
          100
      ) || 0
    : 85 // Default value

  if (loading) {
    return (
      <div className='flex h-screen items-center justify-center bg-[#edeff3]'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4'></div>
          <p className='text-gray-600'>Loading analytics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex h-screen items-center justify-center bg-[#edeff3]'>
        <div className='text-center'>
          <p className='text-red-600 mb-4'>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className='px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600'
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      {/* Main Content */}
      <div className='p-4 md:p-6 lg:p-8'>
        {/* Header Card */}
        <div className='bg-white rounded-3xl p-6 md:p-8 mb-6'>
          {/* Top Row - Greeting and User Profile */}
          <div className='flex items-start justify-between mb-8'>
            {/* Greeting */}
            <div className='text-start'>
              <p className='text-[20px] text-[#434343] font-normal leading-8 mb-1'>Hi {displayName}</p>
              <h1 className='text-[24px] font-bold text-[#151515] leading-8'>Let's make impact today?</h1>
            </div>

            {/* User Profile (Desktop) */}
            <div className='hidden lg:flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                  {initials}
                </div>
                <span className='text-[20px] font-bold text-[#353535] leading-8'>{displayName}</span>
              </div>
              <button className='w-[41px] h-[39px] bg-[#e2e3e7] rounded-full flex items-center justify-center'>
                <ChevronDown className='h-4 w-4 text-gray-600' />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <Sheet>
              <SheetTrigger asChild>
                <button className='lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors'>
                  <Menu className='h-6 w-6 text-gray-700' />
                </button>
              </SheetTrigger>
              <SheetContent side='left' className='p-0 w-[280px]'>
                <OrganizationSidebarContent />
              </SheetContent>
            </Sheet>
          </div>

          {/* Stats Grid */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6'>
            {stats.map((stat, index) => (
              <StatCard key={index} {...stat} />
            ))}
          </div>
        </div>

        {/* Charts Grid */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-6'>
          {/* Left Column - Low Rating Distribution and Age Distribution */}
          <div className='lg:col-span-3 space-y-6'>
            {/* Low Rating Distribution */}
            <div className='bg-white rounded-3xl p-6'>
              <h3 className='text-[20px] font-bold text-[#353535] leading-8 mb-2'>Low Rating Distribution</h3>
              <p className='text-[14px] text-[#4e4d4d] font-normal leading-6 mb-4'>
                Percentage distribution of the most failed assessment modules
              </p>

              {/* Filter Buttons */}
              <div className='flex gap-2 mb-6'>
                <button
                  onClick={() => setGenderFilter('all')}
                  className={`px-4 py-1 rounded-full text-[12px] font-normal leading-4 transition-colors ${
                    genderFilter === 'all'
                      ? 'bg-[#FF6600] text-white'
                      : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setGenderFilter('male')}
                  className={`px-4 py-1 rounded-full text-[12px] font-normal leading-4 transition-colors ${
                    genderFilter === 'male'
                      ? 'bg-[#FF6600] text-white'
                      : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
                  }`}
                >
                  Male
                </button>
                <button
                  onClick={() => setGenderFilter('female')}
                  className={`px-4 py-1 rounded-full text-[12px] font-normal leading-4 transition-colors ${
                    genderFilter === 'female'
                      ? 'bg-[#FF6600] text-white'
                      : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
                  }`}
                >
                  Female
                </button>
              </div>

              <DonutChart data={lowRatingData} />
            </div>

            {/* Age Distribution */}
            <div className='bg-white rounded-3xl p-6'>
              <h3 className='text-[20px] font-bold text-[#353535] leading-8 mb-4'>Age Distribution</h3>
              <DonutChart data={ageDistributionData} />
            </div>
          </div>

          {/* Middle Column - Graphical Distribution */}
          <div className='lg:col-span-6 bg-white rounded-3xl p-6'>
            <h3 className='text-[20px] font-bold text-[#353535] leading-8 mb-2'>
              Graphical Distribution Of Performance Overview
            </h3>
            <p className='text-[14px] text-[#4e4d4d] font-normal leading-6 mb-4'>
              Average Couple Evaluation Score from the Intimate Partner Violence (IPV) Assessment
            </p>

            {/* Filter Buttons */}
            <div className='flex gap-2 mb-6'>
              <button
                onClick={() => setPerformanceFilter('all')}
                className={`px-4 py-1 rounded-full text-[12px] font-normal leading-4 transition-colors ${
                  performanceFilter === 'all'
                    ? 'bg-[#FF6600] text-white'
                    : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setPerformanceFilter('male')}
                className={`px-4 py-1 rounded-full text-[12px] font-normal leading-4 transition-colors ${
                  performanceFilter === 'male'
                    ? 'bg-[#FF6600] text-white'
                    : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
                }`}
              >
                Male
              </button>
              <button
                onClick={() => setPerformanceFilter('female')}
                className={`px-4 py-1 rounded-full text-[12px] font-normal leading-4 transition-colors ${
                  performanceFilter === 'female'
                    ? 'bg-[#FF6600] text-white'
                    : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
                }`}
              >
                Female
              </button>
            </div>

            <div className='h-[450px]'>
              <BarChart data={performanceData} maxValue={maxPerformanceValue} />
            </div>
          </div>

          {/* Right Column - Impact Report */}
          <div className='lg:col-span-3 bg-white rounded-3xl p-6'>
            <h3 className='text-[20px] font-bold text-[#353535] leading-8 mb-4'>Impact Report</h3>
            <p className='text-[14px] text-[#4e4d4d] font-normal leading-6 mb-6'>
              Lorem ipsum dolor sit amet consectetur. Adipiscing porta habitant ullamcorper mauris ac nam magna. Vitae
              mauris a ut sem pretium euismod faucibus velit.
            </p>

            <div className='flex justify-center mb-6'>
              <GaugeChart value={impactRating} />
            </div>

            <p className='text-[14px] text-[#4e4d4d] font-normal leading-6'>
              Lorem ipsum dolor sit amet consectetur. Adipiscing porta habitant ullamcorper mauris ac nam magna. Vitae
              mauris a ut sem pretium euismod faucibus velit.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
